import { PatientFormData } from "components/views/patientDetails/patientEdit";
import {
  ExhPatientCreateData,
  ExhPatientData,
  PatientData,
  PatientsListEntry,
  PreferredContactMethod,
  Sex,
} from "types/patient";

/**
 * Map a backend (snake_case) patient field name to the corresponding Formik
 * field path (dot-notation, camelCase). Returns undefined for unknown keys so
 * callers can decide whether to fall back to a non-field-level error.
 */
export const patientErrorFieldConverter = (backendField: string): string | undefined => {
  return {
    /* eslint-disable camelcase */
    nhs_number: "nhsNumber",
    hospital_number: "hospitalNumber",
    date_of_birth: "dateOfBirth",
    sex: "sex",
    oncologist: "oncologist",
    name: "firstName",
    email: "contactDetails.email",
    phone_number: "contactDetails.primaryContact",
    secondary_contact: "contactDetails.secondaryContact",
    preferred_contact_method: "contactDetails.preferredContactMethod",
    address_line1: "contactDetails.address.line1",
    address_line2: "contactDetails.address.line2",
    address_city: "contactDetails.address.city",
    address_state: "contactDetails.address.state",
    address_postcode: "contactDetails.address.postcode",
    address_country_code: "contactDetails.address.countryCode",
    consent_service: "consent.service",
    consent_privacy_policy: "consent.privacyPolicy",
    consent_feedback: "consent.feedback",
    consent_research: "consent.research",
    /* eslint-enable camelcase */
  }[backendField];
};

/**
 * Convert PatientFormData to ExtraHorizon patient document format for create/update operations
 */
export const convertToExhPatientData = (formData: PatientFormData): ExhPatientCreateData => {
  /* eslint-disable camelcase */
  const address = formData.contactDetails?.address;
  return {
    name: `${formData.firstName} ${formData.lastName}`.trim(),
    email: formData.contactDetails?.email || undefined,
    nhs_number: formData.nhsNumber,
    hospital_number: formData.hospitalNumber,
    date_of_birth: formData.dateOfBirth,
    sex: formData.sex,
    phone_number: formData.contactDetails?.primaryContact || undefined,
    oncologist: formData.oncologist || undefined,
    address_line1: address?.line1 || undefined,
    address_line2: address?.line2 || undefined,
    address_city: address?.city || undefined,
    address_state: address?.state || undefined,
    address_postcode: address?.postcode || undefined,
    address_country_code: address?.countryCode || undefined,
    secondary_contact: formData.contactDetails?.secondaryContact || undefined,
    preferred_contact_method: formData.contactDetails?.preferredContactMethod || undefined,
    consent_service: formData.consent?.service,
    consent_privacy_policy: formData.consent?.privacyPolicy,
    consent_feedback: formData.consent?.feedback,
    consent_research: formData.consent?.research,
  };
  /* eslint-enable camelcase */
};

/**
 * Convert ExtraHorizon patient document to PatientsListEntry format
 */
export const convertToPatientListEntry = (exhPatient: ExhPatientData): PatientsListEntry => {
  const data = exhPatient.data || {};

  // API returns combined "name" field - split into first/last
  const nameParts = data.name?.split(" ") || [];
  const firstName = nameParts[0] || "";
  const lastName = nameParts.slice(1).join(" ") || "";

  return {
    id: exhPatient.id,
    firstName,
    lastName,
    nhsNumber: data.nhs_number || "",
    hospitalNumber: data.hospital_number || "",
    dateOfBirth: data.date_of_birth || "",
    sex: (data.sex as Sex) || Sex.Male,
    entiaPatientId: data.entia_patient_id,
    latestResult: undefined, // Will be populated separately if needed
    lastMeasurementResultId: data.last_measurement_result_id,
    deviceMeasurementIds: data.device_measurement_ids,
  };
};

/**
 * Convert ExtraHorizon patient document to full PatientData format for patient details page
 *
 * Note: Some fields have hardcoded defaults because the ExH API does not return them:
 * - consent: Managed separately, not part of patient document
 * - trainingDate: API only has `trained` boolean, no date
 */
export const convertExhToPatientData = (exhPatient: ExhPatientData): PatientData => {
  const data = exhPatient.data || {};

  // API returns combined "name" field - split into first/last
  const nameParts = data.name?.split(" ") || [];
  const firstName = nameParts[0] || "";
  const lastName = nameParts.slice(1).join(" ") || "";

  return {
    id: exhPatient.id,
    patientId: data.patient_id,
    firstName,
    lastName,
    nhsNumber: data.nhs_number || "",
    hospitalNumber: data.hospital_number || "",
    dateOfBirth: data.date_of_birth || "",
    sex: (data.sex as Sex) || Sex.Male,
    entiaPatientId: data.entia_patient_id,
    oncologist: data.oncologist || null,
    contactDetails: {
      email: data.email || "",
      primaryContact: data.phone_number || "",
      secondaryContact: data.secondary_contact || "",
      address: {
        countryCode: data.address_country_code || "",
        city: data.address_city || "",
        line1: data.address_line1 || "",
        line2: data.address_line2 || "",
        state: data.address_state || "",
        postcode: data.address_postcode || "",
      },
      preferredContactMethod: data.preferred_contact_method as PreferredContactMethod | undefined,
    },
    // API only has `trained` boolean, no training date
    trainingDate: null,
    enrollmentDate: data.enrollment_date || null,
    enrolledBy: data.enrolled_by ? { id: "", firstName: data.enrolled_by, lastName: "" } : null,
    disEnrollmentDate: data.withdrawn_date || null,
    disEnrolledBy: data.withdrawn_by ? { id: "", firstName: data.withdrawn_by, lastName: "" } : null,
    latestResult: null,
    testResults: null,
    lastMeasurementResultId: data.last_measurement_result_id,
    deviceMeasurementIds: data.device_measurement_ids,
    // Consent managed separately, not part of ExH patient document
    consent: {
      service: false,
      serviceLastUpdate: [],
      privacyPolicy: false,
      privacyPolicyLastUpdate: [],
      feedback: false,
      feedbackLastUpdate: [],
      research: false,
      researchLastUpdate: [],
    },
  };
};
